// apps/accounts/src/pages/materials/UOMs.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Edit, Trash2, X, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

const UOMs = () => {
  const { role } = useAuth();
  const canManage = ['admin', 'accounts'].includes(role);

  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', symbol: '', is_base: false });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchData = async () => {
    try {
      const records = await pb.collection('material_uoms').getFullList({ sort: 'name', requestKey: null });
      setUoms(records);
    } catch (err) { if (err.isAbort) return; toast.error('Failed to load UOMs'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this UOM?')) return;
    try { await pb.collection('material_uoms').delete(id); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({ name: '', symbol: '', is_base: false });
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (uom) => {
    setEditing(uom);
    setFormData({ name: uom.name, symbol: uom.symbol, is_base: uom.is_base || false });
    setFormError('');
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    try {
      const data = { name: formData.name.trim(), symbol: formData.symbol.trim(), is_base: formData.is_base };
      if (editing) {
        await pb.collection('material_uoms').update(editing.id, data);
        toast.success('UOM updated');
      } else {
        await pb.collection('material_uoms').create(data);
        toast.success('UOM created');
      }
      setShowForm(false);
      fetchData();
    } catch (err) {
      let msg = err.message || 'Failed';
      if (err.data?.data) {
        const fields = Object.entries(err.data.data).map(([k, v]) => `${k}: ${v.message}`).join(', ');
        msg += ` (${fields})`;
      }
      setFormError(msg);
      setFormLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400" /></div>;

  const filtered = uoms.filter(u => u.name?.toLowerCase().includes(search.toLowerCase()) || u.symbol?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Units of Measure</h1>
          <p className="text-sm text-slate-500">Manage material units</p>
        </div>
        {canManage && (
          <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-white">
            <Plus className="w-4 h-4 mr-1" /> Add UOM
          </Button>
        )}
      </div>

      <div className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm border">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search UOM..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Button variant="outline" onClick={() => setSearch('')}>Clear</Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Name</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Symbol</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Base Unit</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.length === 0 ? <tr><td colSpan="4" className="px-4 py-8 text-center text-slate-500">No UOMs</td></tr> : filtered.map(u => (
                <tr key={u.id} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3">{u.symbol}</td>
                  <td className="px-4 py-3">{u.is_base ? '✅ Yes' : '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-1">
                      {canManage && (
                        <>
                          <button onClick={() => openEdit(u)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(u.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">{editing ? 'Edit UOM' : 'New UOM'}</h2>
            {formError && <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200">{formError}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                <Input name="name" value={formData.name} onChange={handleChange} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Symbol *</label>
                <Input name="symbol" value={formData.symbol} onChange={handleChange} required />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" name="is_base" checked={formData.is_base} onChange={handleChange} id="is_base" />
                <label htmlFor="is_base" className="text-sm text-slate-700">Base Unit</label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editing ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UOMs;